/**
 * Romeo Delauris product gallery zoom v32
 * One delegated controller for desktop hover zoom and mobile tap/drag zoom.
 */
(function () {
  'use strict';

  if (window.RDProductZoomV32) return;
  window.RDProductZoomV32 = true;

  var SECTION_SELECTOR = '[data-section-type="product"].product--rd-luxury';
  var MEDIA_SELECTOR = '.product-media, .product-media-new, .js-product-media-container';
  var IMAGE_SELECTOR = '.product-media__image, .product-media-new__image';
  var FRAME_SELECTOR = '.js-product-media-item-variant, .product-media__slider-slide-wrapper, .product-media-new__slider-slide-wrapper, .product-media__item, .product-media-new__item, .js-product-gallery-slide';
  var THUMB_SELECTOR = '.product-media__slider-thumbnails, .product-media-new__slider-thumbnails, .product-media__slider-thumbnails-stacked, .product-media__thumb, .product-media-new__thumb, .thumbnail, [data-thumbnail]';
  var NON_IMAGE_MEDIA_SELECTOR = 'video, iframe, model-viewer, .js-video-wrapper, [data-zoom-media-html-video], .product-media__model, .product-media__video, .js-product-media-model-content';
  var DESKTOP_QUERY = window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)');
  var active = null;
  var touch = null;

  function isDesktopEnabled(section) {
    return !!(section && DESKTOP_QUERY.matches && section.classList.contains('product--rd-image-zoom-desktop'));
  }

  function isMobileEnabled(section) {
    return !!(section && !DESKTOP_QUERY.matches && section.classList.contains('product--rd-image-zoom-mobile'));
  }

  function getMedia(section) {
    return section ? section.querySelector(MEDIA_SELECTOR) : null;
  }

  function getImage(target, section) {
    if (!target || !target.closest || !section) return null;
    var image = target.matches && target.matches(IMAGE_SELECTOR) ? target : target.closest(IMAGE_SELECTOR);
    if (!image || !section.contains(image) || image.closest(THUMB_SELECTOR)) return null;

    var slide = image.closest('.js-product-gallery-slide, .product-media__slider-slide, .product-media-new__slider-slide');
    if (slide && slide.querySelector(NON_IMAGE_MEDIA_SELECTOR)) return null;
    return image;
  }

  function getFrame(image) {
    if (!image) return null;
    var frame = image.closest(FRAME_SELECTOR) || image.parentElement;
    if (frame && frame.tagName && frame.tagName.toLowerCase() === 'picture') frame = frame.parentElement;
    return frame;
  }

  function currentImageSource(image) {
    return image ? (image.currentSrc || image.getAttribute('src') || image.getAttribute('data-src') || '') : '';
  }

  function largestSourceFromSet(srcset) {
    if (!srcset) return '';
    var best = null;
    srcset.split(',').forEach(function (candidate, index) {
      var parts = candidate.trim().split(/\s+/);
      if (!parts[0]) return;
      var score = index + 1;
      if (parts[1] && /w$/i.test(parts[1])) score = parseFloat(parts[1]) || score;
      if (parts[1] && /x$/i.test(parts[1])) score = (parseFloat(parts[1]) || 1) * 10000;
      if (!best || score > best.score) best = { source: parts[0], score: score };
    });
    return best ? best.source : '';
  }

  function highResolutionSource(image) {
    if (!image) return '';
    return image.getAttribute('data-rd-hirez-zoom-src') ||
      image.getAttribute('data-zoom-src') ||
      largestSourceFromSet(image.getAttribute('data-srcset') || image.getAttribute('srcset')) ||
      currentImageSource(image);
  }

  function copyImagePresentation(source, destination) {
    if (!source || !destination || !window.getComputedStyle) return;
    var style = window.getComputedStyle(source);
    destination.style.objectFit = style.objectFit || 'contain';
    destination.style.objectPosition = style.objectPosition || '50% 50%';
  }

  function loadHighResolutionSource(image, layerImage) {
    if (!image || !layerImage) return;
    var preview = currentImageSource(image);
    var highResolution = highResolutionSource(image);

    if (!layerImage.getAttribute('src') && preview) layerImage.setAttribute('src', preview);
    if (!highResolution || layerImage.dataset.rdZoomRequested === highResolution) return;

    layerImage.dataset.rdZoomRequested = highResolution;
    var preloader = new Image();
    preloader.decoding = 'async';
    preloader.onload = function () {
      if (!layerImage.isConnected || layerImage.dataset.rdZoomRequested !== highResolution) return;
      layerImage.setAttribute('src', highResolution);
      layerImage.classList.add('is-high-resolution');
    };
    preloader.src = highResolution;
  }

  function ensureLayer(image) {
    var frame = getFrame(image);
    if (!frame) return null;

    frame.classList.add('rd-v32-zoom-frame');
    if (window.getComputedStyle(frame).position === 'static') frame.style.position = 'relative';

    var layer = Array.prototype.slice.call(frame.children).find(function (child) {
      return child.classList && child.classList.contains('rd-v32-zoom-layer');
    });
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'rd-v32-zoom-layer';
      layer.setAttribute('aria-hidden', 'true');
      layer.innerHTML = '<img class="rd-v32-zoom-image" alt="" draggable="false" decoding="async">';
      frame.appendChild(layer);
    }

    var layerImage = layer.querySelector('.rd-v32-zoom-image');
    copyImagePresentation(image, layerImage);
    loadHighResolutionSource(image, layerImage);
    return { frame: frame, layer: layer, image: image };
  }

  function pointFromEvent(event) {
    return { x: event && typeof event.clientX === 'number' ? event.clientX : 0, y: event && typeof event.clientY === 'number' ? event.clientY : 0 };
  }

  function setOrigin(state, event) {
    if (!state || !state.frame || !event) return;
    var rect = state.frame.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var point = pointFromEvent(event);
    var x = Math.max(0, Math.min(100, ((point.x - rect.left) / rect.width) * 100));
    var y = Math.max(0, Math.min(100, ((point.y - rect.top) / rect.height) * 100));
    state.frame.style.setProperty('--rd-v32-zoom-x', x.toFixed(2) + '%');
    state.frame.style.setProperty('--rd-v32-zoom-y', y.toFixed(2) + '%');
  }

  function deactivate(section) {
    if (!active || (section && active.section !== section)) return;
    active.frame.classList.remove('is-rd-v32-zoomed');
    active.frame.style.removeProperty('--rd-v32-zoom-x');
    active.frame.style.removeProperty('--rd-v32-zoom-y');
    active.image.classList.remove('is-rd-v32-zoom-source');
    active.section.classList.remove('rd-v32-zoom-is-active');
    active = null;
  }

  function activate(section, image, event) {
    if (!section || !image) return;
    if (active && (active.image !== image || active.section !== section)) deactivate();
    var state = ensureLayer(image);
    if (!state) return;
    state.section = section;
    active = state;
    setOrigin(state, event);
    state.frame.classList.add('is-rd-v32-zoomed');
    state.image.classList.add('is-rd-v32-zoom-source');
    section.classList.add('rd-v32-zoom-is-active');
  }

  function move(section, image, event) {
    if (!active || active.section !== section || active.image !== image) activate(section, image, event);
    if (active) setOrigin(active, event);
  }

  function cancelNativeImageAction(event, section) {
    if (!section.classList.contains('product--rd-image-zoom-no-modal')) return;
    if (!getImage(event.target, section)) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
  }

  function bindSection(section) {
    if (!section || section.dataset.rdProductZoomV32 === 'true') return;
    var media = getMedia(section);
    if (!media) return;

    section.dataset.rdProductZoomV32 = 'true';

    media.addEventListener('pointerover', function (event) {
      if (event.pointerType === 'touch' || !isDesktopEnabled(section)) return;
      var image = getImage(event.target, section);
      if (!image) return;
      activate(section, image, event);
    });

    media.addEventListener('pointermove', function (event) {
      var image = getImage(event.target, section);

      if (event.pointerType !== 'touch') {
        if (image && isDesktopEnabled(section)) move(section, image, event);
        return;
      }

      if (!touch || touch.pointerId !== event.pointerId) return;
      var dx = event.clientX - touch.startX;
      var dy = event.clientY - touch.startY;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) touch.moved = true;
      if (!active || active.image !== touch.image || !isMobileEnabled(section)) return;
      if (event.cancelable) event.preventDefault();
      setOrigin(active, event);
    }, { passive: false });

    media.addEventListener('pointerout', function (event) {
      if (event.pointerType === 'touch' || !active || active.section !== section) return;
      if (!active.frame.contains(event.relatedTarget)) deactivate(section);
    });

    media.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'touch' || !isMobileEnabled(section)) return;
      var image = getImage(event.target, section);
      if (!image) return;
      touch = {
        section: section,
        image: image,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        moved: false
      };
      if (active && active.image === image) {
        if (image.setPointerCapture) image.setPointerCapture(event.pointerId);
        if (event.cancelable) event.preventDefault();
        setOrigin(active, event);
      }
    }, { passive: false });

    media.addEventListener('pointerup', function (event) {
      if (event.pointerType !== 'touch' || !touch || touch.pointerId !== event.pointerId) return;
      var gesture = touch;
      touch = null;
      if (!isMobileEnabled(section)) return;
      if (event.cancelable && !gesture.moved) event.preventDefault();
      if (gesture.moved) return;
      if (active && active.image === gesture.image) deactivate(section);
      else activate(section, gesture.image, event);
    }, { passive: false });

    media.addEventListener('pointercancel', function (event) {
      if (touch && touch.pointerId === event.pointerId) touch = null;
    });

    media.addEventListener('click', function (event) {
      cancelNativeImageAction(event, section);
    }, true);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches(SECTION_SELECTOR)) bindSection(scope);
    scope.querySelectorAll(SECTION_SELECTOR).forEach(bindSection);
  }

  function resetForViewportChange() {
    deactivate();
    touch = null;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);

  document.addEventListener('theme:all:loaded', function () { window.setTimeout(function () { init(document); }, 80); });
  document.addEventListener('product:loaded', function () { window.setTimeout(function () { init(document); }, 80); });
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    if (active && event.target && event.target.contains(active.section)) deactivate();
  });

  ['variant:change', 'variant:changed', 'product:variant-change'].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      deactivate();
      window.setTimeout(function () { init(document); }, 60);
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') deactivate();
  });

  document.addEventListener('pointerdown', function (event) {
    if (active && active.frame && !active.frame.contains(event.target)) deactivate();
  }, true);

  if (typeof DESKTOP_QUERY.addEventListener === 'function') DESKTOP_QUERY.addEventListener('change', resetForViewportChange);
  else if (typeof DESKTOP_QUERY.addListener === 'function') DESKTOP_QUERY.addListener(resetForViewportChange);
})();
